import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";

const SESSION_KEY = "ip_location_cache";

export function useGeolocation() {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState("prompt"); // prompt, granted, denied
  const [ipLocation, setIpLocation] = useState(null);

  // Restore cached IP location from sessionStorage
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) setIpLocation(JSON.parse(cached));
    } catch { /* ignore */ }
  }, []);

  const fetchIpFallback = useCallback(async () => {
    // Check sessionStorage first
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        setIpLocation(parsed);
        if (parsed.latitude && parsed.longitude) {
          setCoords({ lat: parsed.latitude, lng: parsed.longitude, approximate: true });
        }
        return;
      }
    } catch { /* ignore */ }

    try {
      const res = await axios.get(`${API}/geo/ip-locate`);
      const data = res.data;
      if (data?.city) {
        setIpLocation(data);
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
        if (data.latitude && data.longitude) {
          setCoords({ lat: data.latitude, lng: data.longitude, approximate: true });
        }
      }
    } catch { /* silent fallback */ }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      fetchIpFallback();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setPermission("granted");
        localStorage.setItem("gps_permission", "granted");
      },
      (err) => {
        setError(err.message);
        setPermission("denied");
        localStorage.setItem("gps_permission", "denied");
        fetchIpFallback();
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [fetchIpFallback]);

  // Auto-request if previously granted, or fetch IP fallback if denied
  useEffect(() => {
    const prev = localStorage.getItem("gps_permission");
    if (prev === "granted") {
      requestLocation();
    } else if (prev === "denied") {
      setPermission("denied");
      fetchIpFallback();
    }
  }, [requestLocation, fetchIpFallback]);

  // Refresh location periodically (every 5 min) if granted
  useEffect(() => {
    if (permission !== "granted") return;
    const interval = setInterval(requestLocation, 300000);
    return () => clearInterval(interval);
  }, [permission, requestLocation]);

  return { coords, error, permission, requestLocation, ipLocation };
}
